/**
 * Dashboard route registration — composition shell wires URL search to
 * DashboardRoute. router.tsx lists this route next to area, sector, and stock.
 */
import {
  createRoute,
  stripSearchParams,
  useNavigate,
  type SearchMiddleware,
} from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';

import {
  DEFAULT_TAXONOMY,
  DashboardSearchParamsSchema,
  TREEMAP_EXPAND_ON,
  type AreaId,
  type DashboardSearchParams,
  type SectorId,
  type SortState,
  type Ticker,
} from '@meridian/contracts';

import { Skeleton } from '@/components/primitives/skeleton';
import { loadConfig } from '@/config';
import { dashboardSearchWrite, type DashboardSearchWrite } from '@/lib/nav/search';
import { areaTarget, sectorTarget, stockTarget } from '@/lib/nav/targets';
import { rootRoute } from '@/router-root';
import { useTaxonomyParam } from '@/state/use-taxonomy-param';

const config = loadConfig();

const loadDashboardRoute = () => import('./dashboard-route');

const DashboardScreen = lazy(async () => {
  const module = await loadDashboardRoute();
  return { default: module.DashboardRoute };
});

/**
 * Runs after validateSearch on navigate. Zod's `.default('sector')` would
 * otherwise pin `tax=sector` into the query string on every in-place write.
 */
export const dashboardSearchMiddlewares: readonly SearchMiddleware<DashboardSearchParams>[] = [
  stripSearchParams({ tax: DEFAULT_TAXONOMY }),
];

/**
 * Pure search patch for sortA / sortB URL writes (unit-testable).
 *
 * Typed against `DashboardSearchParams` rather than `Record<string, unknown>`:
 * the erased form is what forced a `@ts-expect-error` at both call sites, and a
 * suppression on the URL write path outlives its cause — rename `sortA` in the
 * schema and this would still compile, with Zod's `.catch()` quietly swallowing
 * the unknown field into the default sort on read.
 */
export function patchSortSearch(
  field: 'sortA' | 'sortB',
  sort: SortState,
): (prev: Partial<DashboardSearchParams>) => DashboardSearchWrite {
  /* The router hands `prev` as a PARTIAL, so the patch is re-parsed rather than
     spread-and-asserted: every field carries a `.default()`/`.catch()`, making
     the result total by construction and the write path validated at the same
     boundary the read path is. */
  return (prev) =>
    dashboardSearchWrite(
      DashboardSearchParamsSchema.parse(
        field === 'sortA' ? { ...prev, sortA: sort } : { ...prev, sortB: sort },
      ),
    );
}

export function patchExpandSearch(
  expanded: boolean,
): (prev: Partial<DashboardSearchParams>) => DashboardSearchWrite {
  return (prev) =>
    dashboardSearchWrite(
      DashboardSearchParamsSchema.parse(
        expanded ? { ...prev, exp: TREEMAP_EXPAND_ON } : { ...prev, exp: undefined },
      ),
    );
}

/** Sector detail navigation target — preserves win/idx. */
export function sectorDetailTarget(
  sectorId: SectorId,
  search: Pick<DashboardSearchParams, 'win' | 'idx' | 'book' | 'cmp'>,
): ReturnType<typeof sectorTarget> {
  return sectorTarget(sectorId, search);
}

/** Stock detail navigation target — preserves win/idx; tax only when industry. */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<DashboardSearchParams, 'win' | 'idx' | 'book' | 'cmp'> & {
    readonly tax?: DashboardSearchParams['tax'];
  },
): ReturnType<typeof stockTarget> {
  return stockTarget(ticker, search);
}

/** Area detail navigation target — preserves win/idx and pins industry tax. */
export function areaDetailTarget(
  areaId: AreaId,
  search: Pick<DashboardSearchParams, 'win' | 'idx' | 'book' | 'cmp'>,
): ReturnType<typeof areaTarget> {
  return areaTarget(areaId, search);
}

/**
 * One writer, called twice — the two copies differed only by the field name.
 *
 * `dashboardRoute.useNavigate()`, not the bare `useNavigate()`: unscoped, the
 * router cannot know this route's search schema and narrows a search updater to
 * `never`, which is what the two `@ts-expect-error` suppressions were hiding.
 */
function useSearchSortWriter(field: 'sortA' | 'sortB'): (sort: SortState) => void {
  const navigate = dashboardRoute.useNavigate();
  return useCallback(
    (sort: SortState) => {
      void navigate({ search: patchSortSearch(field, sort), replace: true });
    },
    [navigate, field],
  );
}

function useDashboardSortCallbacks() {
  return {
    onSortAChange: useSearchSortWriter('sortA'),
    onSortBChange: useSearchSortWriter('sortB'),
  };
}

function useTreemapExpandBinding(search: DashboardSearchParams): {
  readonly value: boolean;
  readonly onChange: (expanded: boolean) => void;
} {
  const navigate = dashboardRoute.useNavigate();
  const onChange = useCallback(
    (expanded: boolean) => {
      void navigate({ search: patchExpandSearch(expanded), replace: true });
    },
    [navigate],
  );
  return { value: search.exp === TREEMAP_EXPAND_ON, onChange };
}

function useDashboardDetailNavigation(search: DashboardSearchParams) {
  const navigate = useNavigate();
  const { win, idx, tax, book, cmp } = search;
  const onSectorSelect = useCallback(
    (sectorId: SectorId) => {
      void navigate(sectorDetailTarget(sectorId, { win, idx, book, cmp }));
    },
    [navigate, win, idx, book, cmp],
  );

  const onTickerSelect = useCallback(
    (ticker: Ticker) => {
      void navigate(stockDetailTarget(ticker, { win, idx, tax, book, cmp }));
    },
    [navigate, win, idx, tax, book, cmp],
  );

  const onAreaSelect = useCallback(
    (areaId: AreaId) => {
      void navigate(areaDetailTarget(areaId, { win, idx, book, cmp }));
    },
    [navigate, win, idx, book, cmp],
  );
  return { onSectorSelect, onTickerSelect, onAreaSelect };
}

function DashboardRouteAdapter(): JSX.Element {
  const search = dashboardRoute.useSearch();
  const { setTax } = useTaxonomyParam();
  const { onSortAChange, onSortBChange } = useDashboardSortCallbacks();
  const { onSectorSelect, onTickerSelect, onAreaSelect } = useDashboardDetailNavigation(search);
  const treemapExpand = useTreemapExpandBinding(search);

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading dashboard" />}>
      <DashboardScreen
        window={search.win}
        index={search.idx}
        book={search.book}
        compare={search.cmp}
        tax={search.tax}
        taxonomySwitch={config.taxonomySwitch}
        sortA={search.sortA}
        sortB={search.sortB}
        onSortAChange={onSortAChange}
        onSortBChange={onSortBChange}
        onSectorSelect={onSectorSelect}
        onTickerSelect={onTickerSelect}
        onTaxChange={setTax}
        onAreaSelect={onAreaSelect}
        treemapExpand={treemapExpand}
      />
    </Suspense>
  );
}

export const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>) => DashboardSearchParamsSchema.parse(search),
  search: {
    middlewares: [...dashboardSearchMiddlewares],
  },
  loader: loadDashboardRoute,
  component: DashboardRouteAdapter,
});
